import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  NEVER,
  Subject,
  catchError,
  combineLatest,
  mergeMap,
  of,
  throwError,
  tap
} from 'rxjs';
import { SOCKET_LOCATION_EVENT } from '../../enums/events-buffer/socket/socket-location-event.enum';
import { SOCKET_ROOM } from '../../enums/events-buffer/socket/socket-rooms-enum';
import { Logger } from '../../interfaces/Logger';
import { ILocation } from '../../interfaces/location_legacy.interface'; // TODO: remove after testing carlos.florio
import { SocketErrorResponse } from '../../interfaces/socket-error.model';
import { ISocketPayload } from '../../interfaces/socket-payload.interface';
import { SocketRoomHandlerService } from '../../services/socket/socket-room-handler.service';
import { SocketService } from '../../services/socket/socket.service';
import { SignatureService } from '../service/signature.service';
import { ServiceStateService } from '@features/service/application/services/service-state.service';
import { LocationMapper } from '@features/service/infrastructure/mappers/location.mapper';
import { EVENT_BUS } from '@shared/domain/tokens/event-bus.token';
import { ServiceLocationAddedEvent } from '@platform/event-bus/domain/events/service-location.event';
import { ServiceLocationRemovedEvent } from '@platform/event-bus/domain/events/service-location.event';
import { Location } from '@features/service/domain/models/location.model';

@Injectable({
  providedIn: 'root',
})
export class LocationSocketHandlerService {
  private readonly socketService = inject(SocketService);
  private readonly socketRoomHandlerService = inject(SocketRoomHandlerService);
  private readonly serviceStateService = inject(ServiceStateService);
  private readonly locationMapper = inject(LocationMapper);
  private readonly eventBus = inject(EVENT_BUS);
  private readonly signatureService = inject(SignatureService);
  private readonly logger = inject(Logger);
  private readonly destroyRef = inject(DestroyRef);
  private destroyed$: Subject<void> = new Subject<void>();

  constructor() {
    this.locationRoomListener();
  }

  clearSubscriptions(): void {
    this.destroyed$.next();
  }

  /**
   * Listens to the location room entry
   *
   * @private
   * @memberof LocationSocketHandlerService
   */
  private locationRoomListener(): void {
    this.socketRoomHandlerService
      .listenToRoom(SOCKET_ROOM.locations)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.logger.debug(
            `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 📍] [Room joined, clearing previous subscriptions before reinitializing]`
          );
          this.clearSubscriptions();
        })
      )
      .subscribe(() => {
        this.initializeLocationListeners();
      });
  }

  /**
   * Initializes all location room listeners
   *
   * @private
   * @memberof LocationSocketHandlerService
   */
  private initializeLocationListeners(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location listeners initialized]`
    );
    this.onLocationAddedListener();
    this.onLocationRemovedListener();
    this.onLocationActivatedListener();
    this.onLocationDrawersModifiedListener();
  }

  /**
   * Listens to the socket event that is activated when a location has been added
   *
   * @private
   * @memberof LocationSocketHandlerService
   */
  private onLocationAddedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location added listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_LOCATION_EVENT.added)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const locations = eventResponse.payload.locations as ILocation[];
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (locations?.length) {
              const locationsDomain = this.locationMapper.fromLegacyApiArrayToDomain(locations);

              return combineLatest([
                signature$,
                this.serviceStateService.insertLocations(locationsDomain),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location added socket recieved]`
        );

        this.eventBus.publish(new ServiceLocationAddedEvent());
      });
  }

  private onLocationRemovedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location removed listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_LOCATION_EVENT.removed)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest({
            eventResponse: of(eventResponse),
            currentSignature: this.signatureService.getSignatureFromIndexedDB(),
            currentLocation: of(this.serviceStateService.getCurrentLocation()),
          })
        ),
        mergeMap(({ eventResponse, currentSignature, currentLocation }) => {
          const signature = eventResponse.payload?.signature?.checksum;

          if (signature && signature === currentSignature) {
            return NEVER;
          }

          let signature$ = of('');

          if (signature && signature !== currentSignature) {
            signature$ = this.signatureService.insertSignatureOnDB(signature);
          }

          const locationsToRemove = eventResponse.payload
            .locations_ids as string[];

          if (locationsToRemove?.length && currentLocation) {
            const locationsToRemoveDomain = locationsToRemove.map(id => this.serviceStateService.getAllLocations().find(location => location.id === id)) as Location[];

            this.serviceStateService.removeLocations(locationsToRemoveDomain);

            if (locationsToRemove?.includes(currentLocation?.id)) {
              this.serviceStateService.removeCurrentLocation();
            }

            return signature$;
          }

          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location removed from service]`
        );
        // TODO: remove after testing carlos.florio
        //this.locationService.notifyOnLocationRemoved();
        this.eventBus.publish(new ServiceLocationRemovedEvent());
      });
  }

  private onLocationDrawersModifiedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location drawer modified listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_LOCATION_EVENT.drawers_modified)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            //const locations = eventResponse.payload.locations as ILocation[];
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }


            // TODO: remove after testing carlos.florio
            return signature$;
            /*if (locations) {
              return combineLatest([
                signature$,
                this.locationService.insertLocationsIntoIndexedDB(locations),
              ]);
            }

            return NEVER;*/
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location drawer modified on service]`
        );
        // TODO: remove after testing carlos.florio
        //this.locationService.notifyOnLocationDrawersModified();
      });
  }

  private onLocationActivatedListener(): void {
    this.logger.debug(
      `[Socket LISTENER 🔌👂] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location activated listener initialized]`
    );
    this.socketService
      .receive<ISocketPayload>(SOCKET_LOCATION_EVENT.activated)
      .pipe(
        catchError((error) => throwError(() => new SocketErrorResponse(error))),
        mergeMap((eventResponse: ISocketPayload) =>
          combineLatest([
            of(eventResponse),
            this.signatureService.getSignatureFromIndexedDB(),
          ])
        ),
        mergeMap(
          ([eventResponse, currentSignature]: [ISocketPayload, string]) => {
            const location = eventResponse.payload.location as ILocation;
            const signature = eventResponse.payload?.signature?.checksum;

            if (signature && signature === currentSignature) {
              return NEVER;
            }

            let signature$ = of('');

            if (signature && signature !== currentSignature) {
              signature$ = this.signatureService.insertSignatureOnDB(signature);
            }

            if (location) {
              // TODO: remove after testing carlos.florio
              this.serviceStateService.insertLocations([this.locationMapper.fromLegacyApiToDomain(location)]);

              return combineLatest([
                signature$,
                //this.locationService.insertLocationsIntoIndexedDB([location]),
              ]);
            }

            return NEVER;
          }
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.logger.debug(
          `[Socket 🔌] [Room: ${SOCKET_ROOM.locations} 🏞️] [Location activated on service]`
        );
        // TODO: remove after testing carlos.florio
        //this.locationService.notifyOnLocationActivated();
        this.eventBus.publish(new ServiceLocationAddedEvent());
      });
  }
}
